import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ageGroupsService, categoriesService, nationsService, groupsService, partnerTeamsService } from "../../services";
import { apiClient } from "../../services/apiClient";
import { getAgeGroupColors } from "../../utils/ageGroupColors";
import ConfirmationModal from "./ConfirmationModal";

function PartnerTeamsWizard({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Schritt 1: Altersgruppe
  const [ageGroups, setAgeGroups] = useState([]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);

  // Schritt 2: Partnerform
  const [partnerForms, setPartnerForms] = useState([]);
  const [selectedPartnerForm, setSelectedPartnerForm] = useState(null);

  // Schritt 3: Nation
  const [nations, setNations] = useState([]);
  const [selectedNation, setSelectedNation] = useState(null);

  // Schritt 4: Teams erstellen
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [teams, setTeams] = useState([]); // Array von { id?, participant1Id, participant2Id } - id optional für neue Teams
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingTeamsLoaded, setExistingTeamsLoaded] = useState(false);
  const [originalTeams, setOriginalTeams] = useState([]); // Original geladene Teams für Vergleich
  const [editingTeamId, setEditingTeamId] = useState(null); // ID des Teams, das gerade bearbeitet wird

  // Close Confirmation
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  // Nationen-Suche (Schritt 3)
  const [nationSearchTerm, setNationSearchTerm] = useState('');

  // Undo für Team-Entfernung
  const [removedTeam, setRemovedTeam] = useState(null);
  const [undoTimeout, setUndoTimeout] = useState(null);

  // Slide-Animation
  const [slideDirection, setSlideDirection] = useState('right');

  // Reset beim Öffnen
  useEffect(() => {
    if (isOpen) {
      resetWizard();
      loadInitialData();
    }
  }, [isOpen]);

  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedAgeGroup(null);
    setSelectedPartnerForm(null);
    setSelectedNation(null);
    setSelectedGroup(null);
    setTeams([]);
    setAvailableParticipants([]);
    setError(null);
    setExistingTeamsLoaded(false);
    setOriginalTeams([]);
    setEditingTeamId(null);
    setSelectedParticipant1(null);
    setSelectedParticipant2(null);
    setSearchTerm('');
    setShowCloseConfirmation(false);
    setNationSearchTerm('');
    setRemovedTeam(null);
    if (undoTimeout) clearTimeout(undoTimeout);
    setUndoTimeout(null);
    setSlideDirection('right');
  };

  // Prüfe ob es ungespeicherte Änderungen gibt
  const hasUnsavedChanges = () => {
    if (teams.length !== originalTeams.length) return true;
    // Prüfe ob neue Teams hinzugefügt wurden (ohne ID)
    if (teams.some(t => !t.id)) return true;
    // Prüfe ob existierende Teams entfernt wurden
    const currentIds = new Set(teams.filter(t => t.id).map(t => t.id));
    if (originalTeams.some(o => !currentIds.has(o.id))) return true;
    // Prüfe ob Teilnehmer geändert wurden
    return teams.some(t => {
      if (!t.id) return false;
      const orig = originalTeams.find(o => o.id === t.id);
      if (!orig) return true;
      return t.participant1Id !== orig.participant1Id || t.participant2Id !== orig.participant2Id;
    });
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [ageGroupsResponse, nationsResponse] = await Promise.all([
        ageGroupsService.getAllAgeGroups(),
        nationsService.getAllNations()
      ]);

      if (ageGroupsResponse.success) {
        // Filtere "Alle" aus
        const filtered = ageGroupsResponse.data.filter(ag => ag.name !== 'Alle');
        setAgeGroups(filtered);
      }

      if (nationsResponse.success) {
        setNations(nationsResponse.data);
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error("Fehler beim Laden:", err);
    } finally {
      setLoading(false);
    }
  };

  // Schritt 1: Altersgruppe auswählen
  const handleAgeGroupSelect = async (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
    setError(null);

    // Lade Partnerformen für diese Altersgruppe
    setLoading(true);
    try {
      const response = await categoriesService.getAllCategories();
      if (response.success) {
        // Filtere Partnerformen, die für diese Altersgruppe verfügbar sind
        const forms = response.data.filter(cat => {
          if (cat.type !== 'Partnerform') return false;
          // Prüfe ob Altersgruppe in ageGroups enthalten ist
          const ageGroupIds = cat.ageGroups?.map(ag => typeof ag === 'object' ? ag.id : ag) || [];
          const ageGroupNames = cat.ageGroups?.map(ag => typeof ag === 'object' ? ag.name : ag) || [];
          return ageGroupIds.includes(ageGroup.id) || ageGroupNames.includes(ageGroup.name);
        });
        setPartnerForms(forms);

        if (forms.length === 0) {
          setError('Keine Partnerformen für diese Altersgruppe verfügbar');
        } else {
          // Auto-Weiterschalten nach erfolgreichem Laden
          setTimeout(() => {
            setSlideDirection('right');
            setCurrentStep(2);
          }, 300);
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Partnerformen');
      console.error("Fehler:", err);
    } finally {
      setLoading(false);
    }
  };

  // Schritt 2: Partnerform auswählen
  const handlePartnerFormSelect = (partnerForm) => {
    setSelectedPartnerForm(partnerForm);
    setError(null);
    // Auto-Weiterschalten nach kurzem visuellen Feedback
    setTimeout(() => {
      setSlideDirection('right');
      setCurrentStep(3);
    }, 300);
  };

  // Schritt 3: Nation auswählen
  const handleNationSelect = async (nation) => {
    setSelectedNation(nation);
    setError(null);

    // Lade verfügbare Teilnehmer
    setLoadingParticipants(true);
    let hasError = false;
    try {
      // Finde oder erstelle Gruppe
      let group = null;

      try {
        const createData = await apiClient.get(
          `/groups/find-or-create-partner?categoryId=${selectedPartnerForm.id}&ageGroupId=${selectedAgeGroup.id}`
        );
        // apiClient gibt bereits data zurück wenn success: true
        group = createData?.data || createData;
      } catch (err) {
        console.error("Fehler beim Erstellen der Gruppe:", err);
        setError('Fehler beim Erstellen der Gruppe: ' + (err.message || 'Unbekannter Fehler'));
        setLoadingParticipants(false);
        return;
      }

      if (!group || !group.id) {
        setError('Gruppe konnte nicht gefunden oder erstellt werden');
        setLoadingParticipants(false);
        return;
      }

      setSelectedGroup(group);

      // Lade verfügbare Teilnehmer
      const participantsResponse = await partnerTeamsService.getAvailableParticipants(group.id, nation.id);
      if (participantsResponse.success) {
        setAvailableParticipants(participantsResponse.data || []);
      } else {
        setError(participantsResponse.error || 'Fehler beim Laden der Teilnehmer');
        hasError = true;
      }

      // Lade existierende Teams für diese Gruppe
      if (!existingTeamsLoaded) {
        try {
          const existingTeamsResponse = await partnerTeamsService.getPartnerTeamsByGroup(group.id);
          if (existingTeamsResponse.success && existingTeamsResponse.data) {
            // Filtere Teams nach der ausgewählten Nation
            // Ein Team gehört zur Nation, wenn beide Teilnehmer dieser Nation angehören
            const nationId = nation.id;
            const teamsForNation = existingTeamsResponse.data.filter(team => {
              const p1NationId = team.participant1?.nationId || team.participant1?.nation?.id;
              const p2NationId = team.participant2?.nationId || team.participant2?.nation?.id;
              return p1NationId === nationId && p2NationId === nationId;
            });

            // Transformiere die gefilterten Teams in das erwartete Format
            const transformedTeams = teamsForNation.map(team => ({
              id: team.id,
              participant1Id: team.participant1Id || team.participant1?.id,
              participant2Id: team.participant2Id || team.participant2?.id
            })).filter(team => team.participant1Id && team.participant2Id); // Filtere ungültige Teams

            setTeams(transformedTeams);
            setOriginalTeams(transformedTeams.map(t => ({ ...t }))); // Deep copy für Vergleich

            // Füge Teilnehmer aus existierenden Teams zu availableParticipants hinzu
            // (falls sie nicht bereits enthalten sind), damit sie in der UI angezeigt werden können
            const existingParticipantIds = new Set(availableParticipants.map(p => p.id));
            const participantsFromTeams = [];

            teamsForNation.forEach(team => {
              if (team.participant1 && !existingParticipantIds.has(team.participant1.id)) {
                participantsFromTeams.push(team.participant1);
                existingParticipantIds.add(team.participant1.id);
              }
              if (team.participant2 && !existingParticipantIds.has(team.participant2.id)) {
                participantsFromTeams.push(team.participant2);
                existingParticipantIds.add(team.participant2.id);
              }
            });

            if (participantsFromTeams.length > 0) {
              setAvailableParticipants(prev => [...prev, ...participantsFromTeams]);
            }

            setExistingTeamsLoaded(true);
          }
        } catch (err) {
          console.error("Fehler beim Laden der existierenden Teams:", err);
          // Nicht kritisch - einfach keine Teams anzeigen
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Teilnehmer: ' + (err.message || 'Unbekannter Fehler'));
      console.error("Fehler:", err);
      hasError = true;
    } finally {
      setLoadingParticipants(false);
      // Auto-Weiterschalten zu Schritt 4 nach erfolgreichem Laden
      if (!hasError) {
        setTimeout(() => {
          setSlideDirection('right');
          setCurrentStep(4);
        }, 300);
      }
    }
  };

  // Schritt 4: Teams erstellen
  const [selectedParticipant1, setSelectedParticipant1] = useState(null);
  const [selectedParticipant2, setSelectedParticipant2] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleEditTeam = (team) => {
    setEditingTeamId(team.id);
    setSelectedParticipant1(team.participant1Id);
    setSelectedParticipant2(team.participant2Id);
    setError(null);
  };

  const handleAddTeam = () => {
    if (!selectedParticipant1 || !selectedParticipant2) {
      setError('Bitte wählen Sie beide Teilnehmer aus');
      return;
    }

    if (selectedParticipant1 === selectedParticipant2) {
      setError('Die beiden Teilnehmer müssen unterschiedlich sein');
      return;
    }

    // Speichere editingTeamId in lokaler Variable bevor wir den State ändern
    const currentEditingId = editingTeamId;

    // Wenn ein Team bearbeitet wird, filtere das alte Team heraus
    const filteredTeams = currentEditingId
      ? teams.filter(team => team.id !== currentEditingId)
      : teams;

    // Prüfe ob Team bereits existiert (in der gefilterten Liste)
    const exists = filteredTeams.some(team =>
    ((team.participant1Id === selectedParticipant1 && team.participant2Id === selectedParticipant2) ||
      (team.participant1Id === selectedParticipant2 && team.participant2Id === selectedParticipant1))
    );

    if (exists) {
      setError('Dieses Team existiert bereits');
      return;
    }

    // Wenn ein existierendes Team bearbeitet wird, behalte die ID
    if (currentEditingId) {
      setTeams([...filteredTeams, { id: currentEditingId, participant1Id: selectedParticipant1, participant2Id: selectedParticipant2 }]);
    } else {
      setTeams([...filteredTeams, { participant1Id: selectedParticipant1, participant2Id: selectedParticipant2 }]);
    }

    setSelectedParticipant1(null);
    setSelectedParticipant2(null);
    setEditingTeamId(null);
    setError(null);
  };

  const handleRemoveTeam = (index) => {
    const removed = teams[index];
    setTeams(teams.filter((_, i) => i !== index));

    // Undo-Möglichkeit für 5 Sekunden
    setRemovedTeam({ team: removed, index });
    if (undoTimeout) clearTimeout(undoTimeout);
    const timeout = setTimeout(() => setRemovedTeam(null), 5000);
    setUndoTimeout(timeout);
  };

  const handleUndoRemove = () => {
    if (removedTeam) {
      const newTeams = [...teams];
      newTeams.splice(removedTeam.index, 0, removedTeam.team);
      setTeams(newTeams);
      setRemovedTeam(null);
      if (undoTimeout) clearTimeout(undoTimeout);
      setUndoTimeout(null);
    }
  };

  // Speicher-Zusammenfassung berechnen
  const getSaveStats = () => {
    const newTeams = teams.filter(t => !t.id).length;
    const currentIds = new Set(teams.filter(t => t.id).map(t => t.id));
    const deletedTeams = originalTeams.filter(o => !currentIds.has(o.id)).length;
    const unchanged = teams.filter(t => t.id && originalTeams.some(o =>
      o.id === t.id && o.participant1Id === t.participant1Id && o.participant2Id === t.participant2Id
    )).length;
    const modified = teams.filter(t => t.id).length - unchanged;
    return { newTeams, deletedTeams, unchanged, modified };
  };

  const handleSaveTeams = async () => {
    if (teams.length === 0) {
      setError('Bitte erstellen Sie mindestens ein Team');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const results = [];
      const errors = [];

      // 1. Finde gelöschte Teams (in originalTeams aber nicht in teams)
      const currentTeamIds = new Set(teams.filter(t => t.id).map(t => t.id));
      const deletedTeams = originalTeams.filter(original => !currentTeamIds.has(original.id));

      // Lösche gelöschte Teams
      for (const deletedTeam of deletedTeams) {
        try {
          const deleteResult = await partnerTeamsService.deletePartnerTeam(deletedTeam.id);
          if (!deleteResult.success) {
            errors.push(`Team ${deletedTeam.id} konnte nicht gelöscht werden: ${deleteResult.error}`);
          }
        } catch (err) {
          errors.push(`Team ${deletedTeam.id} konnte nicht gelöscht werden: ${err.message}`);
        }
      }

      // 2. Verarbeite alle Teams
      for (const team of teams) {
        if (!team.id) {
          // Neues Team: Erstelle
          try {
            const result = await partnerTeamsService.createPartnerTeam(
              selectedGroup.id,
              team.participant1Id,
              team.participant2Id
            );
            if (!result.success) {
              errors.push(`Neues Team konnte nicht erstellt werden: ${result.error}`);
            } else {
              results.push(result);
            }
          } catch (err) {
            errors.push(`Neues Team konnte nicht erstellt werden: ${err.message}`);
          }
        } else {
          // Existierendes Team: Prüfe ob geändert
          const originalTeam = originalTeams.find(ot => ot.id === team.id);
          if (originalTeam) {
            // Team ist unverändert, wenn die Teilnehmer identisch sind (gleiche oder getauschte Reihenfolge)
            const isUnchanged =
              (originalTeam.participant1Id === team.participant1Id && originalTeam.participant2Id === team.participant2Id) ||
              (originalTeam.participant1Id === team.participant2Id && originalTeam.participant2Id === team.participant1Id);

            if (!isUnchanged) {
              // Team wurde geändert: Lösche altes und erstelle neues
              try {
                // Lösche altes Team
                const deleteResult = await partnerTeamsService.deletePartnerTeam(team.id);
                if (!deleteResult.success) {
                  errors.push(`Team ${team.id} konnte nicht gelöscht werden: ${deleteResult.error}`);
                  continue;
                }

                // Erstelle neues Team
                const createResult = await partnerTeamsService.createPartnerTeam(
                  selectedGroup.id,
                  team.participant1Id,
                  team.participant2Id
                );
                if (!createResult.success) {
                  errors.push(`Geändertes Team konnte nicht erstellt werden: ${createResult.error}`);
                } else {
                  results.push(createResult);
                }
              } catch (err) {
                errors.push(`Team ${team.id} konnte nicht aktualisiert werden: ${err.message}`);
              }
            }
            // Wenn unverändert, keine Aktion nötig
          }
        }
      }

      if (errors.length > 0) {
        setError(`${errors.length} Fehler beim Speichern: ${errors.join('; ')}`);
      } else {
        // Erfolgreich gespeichert - Wizard direkt schließen (nicht zurück zum Anfang)
        if (onSuccess) {
          onSuccess();
        }
        resetWizard();
        onClose();
      }
    } catch (err) {
      setError('Fehler beim Speichern der Teams: ' + (err.message || 'Unbekannter Fehler'));
      console.error("Fehler:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && !selectedAgeGroup) {
      setError('Bitte wählen Sie eine Altersgruppe aus');
      return;
    }
    if (currentStep === 2 && !selectedPartnerForm) {
      setError('Bitte wählen Sie eine Partnerform aus');
      return;
    }
    if (currentStep === 3 && !selectedNation) {
      setError('Bitte wählen Sie eine Nation aus');
      return;
    }

    setError(null);
    setSlideDirection('right');
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setSlideDirection('left');

    // Abhängige States bereinigen
    if (currentStep === 4) {
      // Von Teams zurück zu Nation: Auswahl-States zurücksetzen
      setSelectedParticipant1(null);
      setSelectedParticipant2(null);
      setEditingTeamId(null);
      setRemovedTeam(null);
      if (undoTimeout) clearTimeout(undoTimeout);
    } else if (currentStep === 3) {
      // Von Nation zurück zu Partnerform: Nation und Teams zurücksetzen
      setSelectedNation(null);
      setTeams([]);
      setOriginalTeams([]);
      setAvailableParticipants([]);
      setExistingTeamsLoaded(false);
      setSelectedGroup(null);
      setNationSearchTerm('');
    } else if (currentStep === 2) {
      // Von Partnerform zurück zu Altersgruppe: Partnerform zurücksetzen
      setSelectedPartnerForm(null);
      setPartnerForms([]);
    }

    setCurrentStep(currentStep - 1);
  };

  const handleClose = () => {
    // Prüfe ob ungespeicherte Änderungen in Schritt 4 vorliegen
    if (currentStep === 4 && hasUnsavedChanges()) {
      setShowCloseConfirmation(true);
    } else {
      resetWizard();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    resetWizard();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 dark:border-slate-700/60">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Partner-Teams erstellen
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Schritt {currentStep} von 4
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-7 h-7 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
            >
              <i className="fas fa-times text-sm"></i>
            </button>
          </div>

          {/* Stepper */}
          <div>
            <div className="relative flex items-center mb-2">
              {[
                { step: 1, label: 'Altersgruppe', icon: 'fa-users' },
                { step: 2, label: 'Partnerform', icon: 'fa-handshake' },
                { step: 3, label: 'Nation', icon: 'fa-flag' },
                { step: 4, label: 'Teams', icon: 'fa-user-plus' }
              ].map((stepInfo, index) => {
                const step = stepInfo.step;
                const isCompleted = step < currentStep;
                const isCurrent = step === currentStep;

                return (
                  <React.Fragment key={step}>
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                        isCompleted
                          ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light text-white'
                          : isCurrent
                            ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light text-white ring-4 ring-primary/20 dark:ring-primary-light/20'
                            : 'bg-white dark:bg-slate-700/80 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500'
                      }`}>
                        {isCompleted
                          ? <i className="fas fa-check text-[11px] font-bold"></i>
                          : <i className={`fas ${stepInfo.icon} text-[11px]`}></i>
                        }
                      </div>
                    </div>
                    {index < 3 && (
                      <div className="flex-1 mx-1.5 h-1 rounded-full transition-all duration-300 overflow-hidden bg-slate-200 dark:bg-slate-700">
                        <div className={`h-full rounded-full transition-all duration-500 ${step < currentStep ? 'w-full bg-primary dark:bg-primary-light' : 'w-0'}`} />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="flex">
              {[
                { step: 1, label: 'Altersgruppe' },
                { step: 2, label: 'Partnerform' },
                { step: 3, label: 'Nation' },
                { step: 4, label: 'Teams' }
              ].map((stepInfo, index) => {
                const isCurrent = stepInfo.step === currentStep;
                const isDone = stepInfo.step < currentStep;
                return (
                  <React.Fragment key={stepInfo.step}>
                    <div className="flex justify-center flex-shrink-0" style={{ width: '2rem' }}>
                      <span className={`text-[10px] whitespace-nowrap transition-colors ${isCurrent ? 'font-semibold text-primary dark:text-primary-light' : isDone ? 'font-medium text-slate-600 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        {stepInfo.label}
                      </span>
                    </div>
                    {index < 3 && <div className="flex-1 mx-1.5" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Breadcrumb Summary - ab Schritt 2 */}
        {currentStep >= 2 && (
          <div className="flex items-center gap-1.5 flex-wrap px-6 py-2.5 border-b border-slate-100 dark:border-slate-700/60">
            {selectedAgeGroup && (
              <button
                onClick={() => { setSlideDirection('left'); setCurrentStep(1); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                <i className="fas fa-users text-[9px] opacity-60" />
                {selectedAgeGroup.name}
              </button>
            )}
            {selectedPartnerForm && currentStep >= 3 && (
              <>
                <i className="fas fa-chevron-right text-[9px] text-slate-300 dark:text-slate-600" />
                <button
                  onClick={() => { setSlideDirection('left'); setCurrentStep(2); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  <i className="fas fa-handshake text-[9px] opacity-60" />
                  {selectedPartnerForm.name}
                </button>
              </>
            )}
            {selectedNation && currentStep >= 4 && (
              <>
                <i className="fas fa-chevron-right text-[9px] text-slate-300 dark:text-slate-600" />
                <button
                  onClick={() => { setSlideDirection('left'); setCurrentStep(3); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  {selectedNation.code && (
                    <img src={process.env.PUBLIC_URL + "/images/flags/" + selectedNation.code + ".png"} alt="" className="w-3.5 h-3.5 rounded-full object-cover opacity-80" />
                  )}
                  {selectedNation.name}
                </button>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6 flex-1 min-h-0 overflow-y-auto" key={currentStep}
          style={{
            animation: `${slideDirection === 'right' ? 'slideInRight' : 'slideInLeft'} 0.25s ease-out`
          }}>
          <style>{`
            @keyframes slideInRight {
              from { opacity: 0; transform: translateX(30px); }
              to { opacity: 1; transform: translateX(0); }
            }
            @keyframes slideInLeft {
              from { opacity: 0; transform: translateX(-30px); }
              to { opacity: 1; transform: translateX(0); }
            }
          `}</style>
          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 border-l-4 border-l-red-500 rounded-xl">
              <div className="flex items-center gap-2.5">
                <i className="fas fa-exclamation-circle text-red-500 dark:text-red-400 text-sm flex-shrink-0"></i>
                <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {/* Schritt 1: Altersgruppe */}
          {currentStep === 1 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3 flex items-center gap-1.5">
                <i className="fas fa-users text-[9px]"></i>
                Altersgruppe wählen
              </p>
              {loading ? (
                <div className="flex items-center justify-center py-12 gap-3">
                  <div className="w-5 h-5 border-2 border-primary dark:border-primary-light border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-slate-500 dark:text-slate-400">Lade…</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                  {ageGroups.map((ageGroup) => {
                    const colors = getAgeGroupColors(ageGroup.name);
                    const isSelected = selectedAgeGroup?.id === ageGroup.id;
                    return (
                      <button
                        key={ageGroup.id}
                        onClick={() => handleAgeGroupSelect(ageGroup)}
                        className={`px-4 py-3.5 rounded-xl border transition-all duration-200 text-left ${isSelected
                          ? `${colors.bg} text-white border-transparent shadow-sm`
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:border-primary/40 dark:hover:border-primary-light/40 hover:shadow-sm'
                        }`}
                      >
                        <div className="text-sm font-semibold">{ageGroup.name}</div>
                        <div className={`text-xs mt-0.5 ${isSelected ? 'opacity-75' : 'text-slate-500 dark:text-slate-400'}`}>
                          {ageGroup.minAge}–{ageGroup.maxAge} Jahre
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Schritt 2: Partnerform */}
          {currentStep === 2 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3 flex items-center gap-1.5">
                <i className="fas fa-handshake text-[9px]"></i>
                Partnerform wählen
              </p>
              {loading ? (
                <div className="flex items-center justify-center py-12 gap-3">
                  <div className="w-5 h-5 border-2 border-primary dark:border-primary-light border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-slate-500 dark:text-slate-400">Lade…</span>
                </div>
              ) : partnerForms.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-handshake text-slate-400 dark:text-slate-500 text-sm"></i>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Keine Partnerformen für diese Altersgruppe verfügbar</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {partnerForms.map((form) => {
                    const isSelected = selectedPartnerForm?.id === form.id;
                    return (
                      <button
                        key={form.id}
                        onClick={() => handlePartnerFormSelect(form)}
                        className={`px-4 py-3.5 rounded-xl border transition-all duration-200 text-left ${isSelected
                          ? 'bg-primary dark:bg-primary-light text-white border-transparent shadow-sm'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:border-primary/40 dark:hover:border-primary-light/40 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <i className={`fas fa-handshake text-sm ${isSelected ? 'opacity-80' : 'text-slate-400 dark:text-slate-500'}`}></i>
                          <span className="text-sm font-semibold">{form.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Schritt 3: Nation */}
          {currentStep === 3 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3 flex items-center gap-1.5">
                <i className="fas fa-flag text-[9px]"></i>
                Nation wählen
              </p>
              <div className="relative mb-3">
                <input
                  type="text"
                  placeholder="Nation suchen…"
                  value={nationSearchTerm}
                  onChange={(e) => setNationSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-4 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[11px]" />
              </div>
              <div className="relative">
                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {nations
                      .filter(n => !nationSearchTerm || n.name.toLowerCase().includes(nationSearchTerm.toLowerCase()))
                      .map((nation) => {
                        const isSelected = selectedNation?.id === nation.id;
                        return (
                          <button
                            key={nation.id}
                            onClick={() => handleNationSelect(nation)}
                            className={`px-3 py-2.5 rounded-xl border transition-all duration-200 flex items-center gap-2.5 text-left ${isSelected
                              ? 'bg-primary/10 dark:bg-primary-light/10 border-primary dark:border-primary-light shadow-sm'
                              : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:border-primary/40 dark:hover:border-primary-light/40 hover:shadow-sm'
                            }`}
                          >
                            {nation.code && (
                              <img
                                src={process.env.PUBLIC_URL + "/images/flags/" + nation.code + ".png"}
                                alt={nation.name}
                                className={`w-6 h-6 object-cover rounded-full border flex-shrink-0 ${isSelected ? 'border-primary/30' : 'border-slate-200 dark:border-slate-600'}`}
                              />
                            )}
                            <span className={`text-sm font-medium truncate ${isSelected ? 'text-primary dark:text-primary-light' : ''}`}>{nation.name}</span>
                            {isSelected && <i className="fas fa-check text-[10px] text-primary dark:text-primary-light ml-auto flex-shrink-0"></i>}
                          </button>
                        );
                      })}
                  </div>
                </div>
                {nations.length > 9 && !nationSearchTerm && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-slate-800 to-transparent rounded-b-xl" />
                )}
              </div>
            </div>
          )}

          {/* Schritt 4: Teams erstellen */}
          {currentStep === 4 && (
            <div className="space-y-4">
              {loadingParticipants ? (
                <div className="flex items-center justify-center py-12 gap-3">
                  <div className="w-5 h-5 border-2 border-primary dark:border-primary-light border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-slate-500 dark:text-slate-400">Lade Teilnehmer…</span>
                </div>
              ) : availableParticipants.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-users text-slate-400 dark:text-slate-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">Keine Teilnehmer gefunden</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                    Keine verfügbaren Teilnehmer für <strong>{selectedPartnerForm?.name}</strong> ({selectedAgeGroup?.name}) aus <strong>{selectedNation?.name}</strong>.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Erstellte Teams */}
                  {teams.length > 0 && (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-700/60">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                          <i className="fas fa-check-circle text-emerald-500 dark:text-emerald-400 text-[10px]"></i>
                          Erstellte Teams
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20">
                          {teams.length}
                        </span>
                      </div>
                      <div className="p-2 space-y-1.5">
                        {teams.map((team, index) => {
                          const p1 = availableParticipants.find(p => p.id === team.participant1Id);
                          const p2 = availableParticipants.find(p => p.id === team.participant2Id);
                          const isExisting = !!team.id;
                          const isEditing = editingTeamId === team.id;
                          return (
                            <div
                              key={team.id || index}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${isEditing
                                ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-300 dark:border-blue-700/50'
                                : 'bg-slate-50/50 dark:bg-slate-700/20 border-slate-200 dark:border-slate-700/50'
                              }`}
                            >
                              <div className="flex-1 flex items-center gap-2 min-w-0 flex-wrap">
                                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{p1?.name}</span>
                                <i className="fas fa-handshake text-[10px] text-slate-300 dark:text-slate-600 flex-shrink-0"></i>
                                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{p2?.name}</span>
                                {isEditing && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50">
                                    <i className="fas fa-pencil text-[8px]"></i>
                                    Bearbeitung
                                  </span>
                                )}
                                {!isEditing && !isExisting && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20">
                                    <i className="fas fa-plus text-[8px]"></i>
                                    Neu
                                  </span>
                                )}
                                {!isEditing && isExisting && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700/50">
                                    <i className="fas fa-check text-[8px]"></i>
                                    Vorhanden
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {isExisting && !isEditing && (
                                  <button
                                    onClick={() => handleEditTeam(team)}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary-light hover:bg-primary/10 dark:hover:bg-primary-light/10 transition-all"
                                    title="Team bearbeiten"
                                  >
                                    <i className="fas fa-pencil text-[11px]"></i>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRemoveTeam(index)}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                  title="Team entfernen"
                                >
                                  <i className="fas fa-times text-[11px]"></i>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Undo-Toast */}
                  {removedTeam && (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 dark:bg-slate-700 text-white rounded-xl shadow-lg" style={{ animation: 'slideInRight 0.2s ease-out' }}>
                      <div className="flex items-center gap-2">
                        <i className="fas fa-times-circle text-xs opacity-60" />
                        <span className="text-sm">Team entfernt</span>
                      </div>
                      <button
                        onClick={handleUndoRemove}
                        className="text-sm font-semibold text-primary-light hover:text-white transition-colors"
                      >
                        Rückgängig
                      </button>
                    </div>
                  )}

                  {/* Teilnehmer-Auswahl */}
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-700/60">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                        <i className="fas fa-user-plus text-[9px]"></i>
                        Team zusammenstellen
                      </span>
                      <span className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">
                        {availableParticipants.length} Athleten
                      </span>
                    </div>

                    <div className="p-3 space-y-3">
                      {/* Suche */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Suchen…"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-8 pr-4 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                        />
                        <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[11px]"></i>
                      </div>

                      {/* Zwei Spalten: Athlet 1 & Athlet 2 */}
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: 'Athlet 1', selectedId: selectedParticipant1, setSelected: setSelectedParticipant1, excludeId: null },
                          { label: 'Athlet 2', selectedId: selectedParticipant2, setSelected: setSelectedParticipant2, excludeId: selectedParticipant1 }
                        ].map(({ label, selectedId, setSelected, excludeId }) => (
                          <div key={label}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                {label}
                              </span>
                              {selectedId && (
                                <button
                                  onClick={() => setSelected(null)}
                                  className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                >
                                  <i className="fas fa-times mr-0.5"></i>
                                  Löschen
                                </button>
                              )}
                            </div>
                            <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-800 space-y-0.5 p-1">
                              {availableParticipants
                                .filter(p => {
                                  if (excludeId && p.id === excludeId) return false;
                                  return !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase());
                                })
                                .map((participant) => {
                                  const isSelected = selectedId === participant.id;
                                  const isUsed = !isSelected && teams.some(team =>
                                    team.participant1Id === participant.id || team.participant2Id === participant.id
                                  );
                                  return (
                                    <button
                                      key={participant.id}
                                      onClick={() => !isUsed && setSelected(participant.id)}
                                      disabled={isUsed}
                                      className={`w-full text-left px-2.5 py-2 rounded-lg text-sm transition-all ${
                                        isSelected
                                          ? 'bg-primary dark:bg-primary-light text-white'
                                          : isUsed
                                            ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-50'
                                            : 'text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className={`inline-flex items-center justify-center flex-shrink-0 h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                                          {participant.participantNumber || participant.id}
                                        </span>
                                        <span className="truncate font-medium">{participant.name}</span>
                                        {isUsed && <i className="fas fa-lock text-[9px] ml-auto flex-shrink-0 opacity-50"></i>}
                                        {isSelected && <i className="fas fa-check text-[10px] ml-auto flex-shrink-0"></i>}
                                      </div>
                                    </button>
                                  );
                                })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Vorschau + Hinzufügen-Button */}
                      <div className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-all ${selectedParticipant1 && selectedParticipant2 ? 'bg-primary/5 dark:bg-primary-light/5 border-primary/30 dark:border-primary-light/30' : 'bg-slate-50 dark:bg-slate-700/20 border-slate-200 dark:border-slate-700/50'}`}>
                        <div className="flex items-center gap-2 min-w-0 flex-1 text-sm">
                          {selectedParticipant1 || selectedParticipant2 ? (
                            <>
                              <span className={`font-semibold truncate ${selectedParticipant1 ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500 italic'}`}>
                                {selectedParticipant1 ? availableParticipants.find(p => p.id === selectedParticipant1)?.name : '–'}
                              </span>
                              <i className="fas fa-handshake text-[10px] text-slate-300 dark:text-slate-600 flex-shrink-0"></i>
                              <span className={`font-semibold truncate ${selectedParticipant2 ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500 italic'}`}>
                                {selectedParticipant2 ? availableParticipants.find(p => p.id === selectedParticipant2)?.name : '–'}
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 dark:text-slate-500">Zwei Athleten auswählen…</span>
                          )}
                        </div>
                        <button
                          onClick={handleAddTeam}
                          disabled={!selectedParticipant1 || !selectedParticipant2}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary dark:bg-primary-light text-white text-xs font-semibold rounded-lg hover:bg-primary-dark dark:hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0"
                        >
                          <i className={`fas ${editingTeamId ? 'fa-check' : 'fa-plus'} text-[10px]`}></i>
                          {editingTeamId ? 'Speichern' : 'Hinzufügen'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <i className="fas fa-arrow-left text-[11px]"></i>
            Zurück
          </button>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !selectedAgeGroup) ||
                (currentStep === 2 && !selectedPartnerForm) ||
                (currentStep === 3 && !selectedNation)
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary dark:bg-primary-light text-white rounded-lg hover:bg-primary-dark dark:hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              Weiter
              <i className="fas fa-arrow-right text-[11px]"></i>
            </button>
          ) : (
            <button
              onClick={handleSaveTeams}
              disabled={saving || teams.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary dark:bg-primary-light text-white rounded-lg hover:bg-primary-dark dark:hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                  Speichere…
                </>
              ) : (() => {
                const stats = getSaveStats();
                const parts = [];
                if (stats.newTeams > 0) parts.push(`${stats.newTeams} neu`);
                if (stats.modified > 0) parts.push(`${stats.modified} geändert`);
                if (stats.deletedTeams > 0) parts.push(`${stats.deletedTeams} gelöscht`);
                const statsText = parts.length > 0 ? ` (${parts.join(', ')})` : '';
                return (
                  <>
                    <i className="fas fa-check text-[11px]"></i>
                    Teams speichern{statsText}
                  </>
                );
              })()}
            </button>
          )}
        </div>
      </div>

      {/* Close Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCloseConfirmation}
        onClose={() => setShowCloseConfirmation(false)}
        onConfirm={handleConfirmClose}
        title="Änderungen verwerfen?"
        message="Es gibt ungespeicherte Änderungen an den Partner-Teams. Möchten Sie den Wizard wirklich schließen und alle Änderungen verwerfen?"
        type="warning"
        confirmText="Verwerfen"
        cancelText="Zurück zum Wizard"
      />
    </div>
  );
}

export default PartnerTeamsWizard;

